/**
 * Configurações locais do Control_Panel, guardadas apenas no `localStorage` do
 * navegador do usuário.
 *
 * Segurança (AD-7): a **API key nunca é embutida no bundle** nem enviada a
 * terceiros. Ela é digitada pelo próprio usuário e permanece só no navegador
 * dele, usada exclusivamente no header `x-api-key` das chamadas à Ingestion_API
 * pública (R19.3). Guardamos também a base URL da API (sobrescrevível) e o
 * plano escolhido (para a validação local de tamanho/formato — R19.5).
 */

import { DEFAULT_API_BASE_URL, normalizeBaseUrl } from "../config/env";
import { DEFAULT_TIER, type Tier, TIERS } from "../config/tiers";

const STORAGE_KEY = "makepub.control-panel.settings.v1";

export interface Settings {
  /** API key do tenant (fornecida pelo usuário; só no navegador dele). */
  readonly apiKey: string;
  /** Base URL da Ingestion_API (sem barra final). */
  readonly apiBaseUrl: string;
  /** Plano do usuário, usado para a validação local antes do envio. */
  readonly tier: Tier;
}

export const DEFAULT_SETTINGS: Settings = {
  apiKey: "",
  apiBaseUrl: DEFAULT_API_BASE_URL,
  tier: DEFAULT_TIER,
};

function safeStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    // localStorage pode lançar em contextos restritos (ex.: modo privado).
    return null;
  }
}

/** Lê as configurações do localStorage, aplicando defaults e saneamento. */
export function loadSettings(): Settings {
  const storage = safeStorage();
  if (!storage) {
    return DEFAULT_SETTINGS;
  }
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_SETTINGS;
    }
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return normalizeSettings(parsed);
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/** Persiste as configurações (saneadas) no localStorage. */
export function saveSettings(settings: Partial<Settings>): Settings {
  const normalized = normalizeSettings(settings);
  const storage = safeStorage();
  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Falha silenciosa: a app segue funcionando com o valor em memória.
    }
  }
  return normalized;
}

/** Remove as configurações locais (ex.: "sair"/limpar credencial). */
export function clearSettings(): void {
  const storage = safeStorage();
  if (storage) {
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}

/** Aplica defaults e normaliza campos vindos de fonte não confiável. */
export function normalizeSettings(input: Partial<Settings> | null | undefined): Settings {
  const apiKey = typeof input?.apiKey === "string" ? input.apiKey.trim() : "";
  const baseUrlRaw =
    typeof input?.apiBaseUrl === "string" && input.apiBaseUrl.trim() !== ""
      ? input.apiBaseUrl
      : DEFAULT_API_BASE_URL;
  const tier: Tier =
    input?.tier && TIERS.includes(input.tier as Tier) ? (input.tier as Tier) : DEFAULT_TIER;
  return {
    apiKey,
    apiBaseUrl: normalizeBaseUrl(baseUrlRaw),
    tier,
  };
}

/** `true` quando há credencial e base URL suficientes para chamar a API. */
export function isConfigured(settings: Settings): boolean {
  return settings.apiKey.length > 0 && settings.apiBaseUrl.length > 0;
}
